import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import type { ConversationItem, Note, Role } from '@chatapp-inbox/shared';
import { Button } from './Button';
import { apiErrorMessage } from './error-message';
import { NoteComposer } from './NoteComposer';
import { NoteItem } from './NoteItem';
import { StatusRegion } from './StatusRegion';
import { useFocusRescue } from './use-focus-rescue';
import { useStatusMessage } from './use-status-message';
import { noteErrorMessage, useCreateNote, useNotes } from './use-notes';
import shared from './shared.module.css';
import styles from './NotesSection.module.css';

const LOAD_ERROR = 'Não foi possível carregar as notas. Tente de novo.';

/** O que dizer no lugar do formulário quando a pessoa não pode escrever na conversa. */
function readOnlyHint(conversation: ConversationItem): string {
  // Sem responsável: ela pode assumir a conversa e então escrever. De outra pessoa: não pode.
  if (!conversation.assignedTo) return 'Assuma a conversa para adicionar notas.';
  return 'Só o responsável pela conversa ou um administrador pode adicionar notas.';
}

/**
 * Seção "Notas internas": aviso de privacidade, formulário (só para quem pode escrever), e a lista
 * da mais nova para a mais antiga. O cache guarda a ordem da API (da mais antiga); a inversão é só
 * de exibição. Mudanças atualizam o cache com a resposta do servidor, sem refazer a lista.
 */
export function NotesSection({
  conversation,
  viewer,
  now,
}: {
  conversation: ConversationItem;
  viewer: { id: string; role: Role };
  now: Date;
}): ReactElement {
  const notes = useNotes(conversation.id);
  const create = useCreateNote(conversation.id);
  const status = useStatusMessage();

  const headingRef = useRef<HTMLHeadingElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const rescue = useFocusRescue(notes.data !== undefined, headingRef);

  const newestFirst = useMemo(() => [...(notes.data ?? [])].reverse(), [notes.data]);

  // Depois de apagar, o foco vai para a nota vizinha (a mais antiga seguinte, ou a mais nova se
  // era a última); sem nenhuma, para o formulário; sem formulário, para o título da seção.
  const [focusAfterDelete, setFocusAfterDelete] = useState<{
    neighborId: string | null;
    n: number;
  } | null>(null);

  const onDeleted = (noteId: string): void => {
    const index = newestFirst.findIndex((note) => note.id === noteId);
    const neighbor = newestFirst[index + 1] ?? newestFirst[index - 1] ?? null;
    setFocusAfterDelete((previous) => ({
      neighborId: neighbor?.id ?? null,
      n: (previous?.n ?? 0) + 1,
    }));
  };

  useEffect(() => {
    if (!focusAfterDelete) return;
    const neighbor = focusAfterDelete.neighborId
      ? Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-note-id]') ?? []).find(
          (element) => element.dataset.noteId === focusAfterDelete.neighborId,
        )
      : null;
    (neighbor ?? composerRef.current ?? headingRef.current)?.focus();
  }, [focusAfterDelete]);

  // Depois de criar, a nota nova (no topo da lista, abaixo do formulário) pode estar fora da área
  // visível do painel: ela é trazida para a vista, SEM tirar o foco do campo. A resposta pode chegar
  // antes ou depois de a lista ser redesenhada com a nota: se ainda não está no DOM, espera o próximo
  // desenho da lista.
  const pendingReveal = useRef<string | null>(null);
  const revealNote = (noteId: string): boolean => {
    const element = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[data-note-id]') ?? [],
    ).find((candidate) => candidate.dataset.noteId === noteId);
    if (!element) return false;
    element.scrollIntoView?.({ block: 'nearest' });
    return true;
  };
  const onCreated = (note: Note): void => {
    status.announce('Nota adicionada.');
    if (!revealNote(note.id)) pendingReveal.current = note.id;
  };
  useEffect(() => {
    if (pendingReveal.current && revealNote(pendingReveal.current)) pendingReveal.current = null;
  });

  const retry = (): void => {
    rescue.arm();
    void notes.refetch().then((result) => {
      if (result.isError) rescue.disarm();
    });
  };

  const createError = create.isError
    ? noteErrorMessage(create.error, 'Não foi possível adicionar a nota. Tente de novo.')
    : null;

  return (
    <section className={shared.section}>
      <h3 ref={headingRef} tabIndex={-1} className={shared.heading}>
        Notas internas
      </h3>
      <p className={styles.privacy}>
        Só a equipe vê estas notas. Elas nunca são enviadas ao cliente.
      </p>

      {conversation.canWrite ? (
        <NoteComposer
          conversationId={conversation.id}
          create={create}
          textareaRef={composerRef}
          onCreated={onCreated}
          onSubmitStart={() => status.announce('')}
        />
      ) : (
        <p className={shared.hint}>{readOnlyHint(conversation)}</p>
      )}

      <StatusRegion message={status.message} />
      {/* Fora do formulário: se a conversa deixou de ser sua (403), o formulário some e o aviso fica. */}
      {createError && (
        <p role="alert" className={shared.alert}>
          {createError}
        </p>
      )}

      {notes.isPending ? (
        <p role="status" className={shared.hint}>
          Carregando notas…
        </p>
      ) : notes.data === undefined ? (
        <div className={styles.loadError}>
          <p role="alert" className={shared.alert}>
            {apiErrorMessage(notes.error, LOAD_ERROR)}
          </p>
          <Button busy={notes.isFetching} onClick={retry}>
            Tentar de novo
          </Button>
        </div>
      ) : newestFirst.length === 0 ? (
        <p className={shared.hint}>Nenhuma nota ainda.</p>
      ) : (
        <ul ref={listRef} className={styles.list}>
          {newestFirst.map((note) => (
            <li key={note.id}>
              <NoteItem
                note={note}
                conversationId={conversation.id}
                viewer={viewer}
                now={now}
                onNotice={status.announce}
                onDeleted={onDeleted}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
